// LIBRARIES
import * as Toast from "@radix-ui/react-toast";
import React from "react";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import { CookieConsentModal } from "../basic/cookies/CookieConsentModal";
import { CookiePrefsModal } from "../basic/cookies/CookiePrefsModal";
import WarningModal from "../basic/create/warningModal/WarningModal";
import VocabTrainingLimitModal from "../basic/create/vocabTrainingLimitModal/VocabTrainingLimitModal";
import Loading from "../basic/loadingAnimation/";
import { AssistantOverview } from "../complex/assistantOverview/AssistantOverview";
import { cp } from "../config";
import { BookContextProvider } from "../context/BookContext";
import { RTContextProvider } from "../context/RTContext";
import { StudentPracticeProvider } from "../context/StudentPracticeContext";
import { StudentReviewTestContextProvider } from "../context/StudentReviewTestContext";
import { TestCreationContextProvider } from "../context/TestCreationContext";
import { TestReviewContextProvider } from "../context/TestReviewContext";
import CreateAnonymousWrapper from "../views/create/createAnonymousWrapper/CreateAnonymousWrapper";
import CreateHome from "../views/create/createHome/CreateHome";
import CreateRtHome from "../views/create/createRtHome/CreateRtHome";
import RecurringTaskSummary from "../views/create/recurringTaskSummary/RecurringTaskSummary";
import SelectBookExperiment from "../views/create/selectBook/SelectBookExperiment";
import SelectBookVocabulary from "../views/create/selectBookVocabulary/SelectBookVocabulary";
import SelectOwnContent from "../views/create/selectOwnContent/SelectOwnContent";
import SelectVocabularySource from "../views/create/selectVocabularySource/SelectVocabularySource";
import SelectVocabularySourceVocabTraining from "../views/create/selectVocabularySource/SelectVocabularySourceVocabTraining";
import Summary from "../views/create/summary/Summary";
import TestPreview from "../views/create/testPreview/TestPreview";
import ReviewIndividualExercise from "../views/review/exercise/ReviewIndividualExercise";
import ReviewListing from "../views/review/listing/ReviewListing";
import ShareTestReviews from "../views/review/share/ShareTestReviews";
import ReviewIndividualStudent from "../views/review/student/ReviewIndividualStudent";
import ReviewTest from "../views/review/test/ReviewTest";
import StudentResult from "../views/studentResult/StudentResult";
import StudentResultAccessGuard from "../views/studentResult/StudentResultAccessGuard";
import StudentResultExercisePage from "../views/studentResult/StudentResultExercisePage";
import Unauthorized from "../views/unauthorized/Unauthorized";
import ClassDetails from "../views/schoolClasses/ClassDetails";
import DownloadCodes from "../views/schoolClasses/DownloadCodes";
import SchoolClasses from "../views/schoolClasses/SchoolClasses";
import AuthWrapper from "../wrapper/authWrapper/AuthWrapper";
import PrivateRoute from "../wrapper/privateRoute/PrivateRoute";
import ReviewTestWrapper from "../wrapper/reviewTestWrapper/ReviewTestWrapper";
import SurvicateWrapper from "../wrapper/survicateWrapper/SurvicateWrapper";
import ScrollToTop from "./ScrollToTop";
import GtmInitializer from "./GtmInitializer";
import PreviousPathTracker from "./PreviousPathTracker";
import RootRedirect from "./RootRedirect";
import AnonymousTestClaimer from "./AnonymousTestClaimer";
import PageViewTracker from "./PageViewTracker";
import SentryReplayGuard from "./SentryReplayGuard";
import { Background } from "./StyledComponents";
import OnlineTest from "../complex/onlineTest/OnlineTest";
import ContentWithHeader from "../wrapper/ContentWithHeader/ContentWithHeader";
import ClassroomLogin from "../views/classroomLogin/ClassroomLogin";
import ClassroomShell from "../views/classroomLogin/ClassroomShell";
import { ClassMemberContextProvider } from "../context/ClassMemberContext";

const TOAST_VIEWPORT_CLASS =
    "fixed top-l left-1/2 -translate-x-1/2 z-[1100] flex flex-col gap-xs max-w-[calc(100vw-var(--spacing-l))] w-max m-no p-no list-none";

export interface LayoutProps {}

const Layout: React.FC<LayoutProps> = (props) => {
    return (
        <Background>
            <BrowserRouter basename={cp.cfg.REACT_APP_PREFIX}>
                <ScrollToTop />
                <GtmInitializer />
                <PreviousPathTracker />
                <PageViewTracker />
                <SentryReplayGuard />
                <Loading />
                <Toast.Provider swipeDirection="up">
                    <Switch>
                        {/* One Route for all class-member areas: a single provider instance stays
                            mounted while navigating between them, so the session is restored once. */}
                        <Route path={["/klassenzimmer", "/student/:testId", "/result/:shareId"]}>
                            <ClassMemberContextProvider>
                                <Switch>
                                    <Route path={"/student/:testId"}>
                                        <TestCreationContextProvider>
                                            <StudentPracticeProvider>
                                                <ContentWithHeader
                                                    isFullWidth
                                                    hasInternalScroll
                                                >
                                                    <OnlineTest />
                                                </ContentWithHeader>
                                            </StudentPracticeProvider>
                                        </TestCreationContextProvider>
                                    </Route>
                                    <Route
                                        path={[
                                            "/klassenzimmer/:schoolClassId/login/:classMemberCode?",
                                            "/klassenzimmer",
                                        ]}
                                        exact
                                    >
                                        <ClassroomLogin />
                                    </Route>
                                    <Route
                                        path="/klassenzimmer/:schoolClassId"
                                        exact
                                    >
                                        <ClassroomShell />
                                    </Route>
                                    <Route path={"/result/:shareId"}>
                                        <StudentReviewTestContextProvider>
                                            <StudentResultAccessGuard>
                                                <Switch>
                                                    <Route path={"/result/:shareId/exercise/:exerciseNumber"}>
                                                        <StudentResultExercisePage />
                                                    </Route>
                                                    <Route path={"/result/:shareId"}>
                                                        <StudentResult />
                                                    </Route>
                                                </Switch>
                                            </StudentResultAccessGuard>
                                        </StudentReviewTestContextProvider>
                                    </Route>
                                    <Redirect to="/klassenzimmer" />
                                </Switch>
                            </ClassMemberContextProvider>
                        </Route>
                        <SurvicateWrapper>
                            <Route
                                exact
                                path="/"
                            >
                                <RootRedirect />
                            </Route>
                            <Route path="/auth/:key?">
                                <AuthWrapper />
                            </Route>
                            <TestCreationContextProvider>
                                <AnonymousTestClaimer />
                                <BookContextProvider>
                                    <RTContextProvider>
                                        <Route path="/assistent">
                                            <CookieConsentModal />
                                            <CookiePrefsModal />
                                            <AssistantOverview />
                                        </Route>
                                        <Route
                                            path="/klassen"
                                            exact
                                        >
                                            <CookieConsentModal />
                                            <CookiePrefsModal />
                                            <SchoolClasses />
                                        </Route>
                                        <Route
                                            path="/klassen/:schoolClassId/codes"
                                            exact
                                        >
                                            <CookieConsentModal />
                                            <CookiePrefsModal />
                                            <DownloadCodes />
                                        </Route>
                                        <Route
                                            path="/klassen/:schoolClassId"
                                            exact
                                        >
                                            <CookieConsentModal />
                                            <CookiePrefsModal />
                                            <ClassDetails />
                                        </Route>
                                        <WarningModal />
                                    </RTContextProvider>
                                </BookContextProvider>
                                <Route path={"/create"}>
                                    <BookContextProvider>
                                        <RTContextProvider>
                                            <Switch>
                                                <Route
                                                    path={"/create"}
                                                    exact
                                                >
                                                    <CookieConsentModal />
                                                    <CookiePrefsModal />
                                                    <CreateHome />
                                                </Route>
                                                <Route
                                                    path={"/create/rt-home"}
                                                    exact
                                                >
                                                    <CookieConsentModal />
                                                    <CookiePrefsModal />
                                                    <CreateRtHome />
                                                </Route>
                                                <Route
                                                    path={"/create/anonymous"}
                                                    exact
                                                >
                                                    <CreateAnonymousWrapper />
                                                </Route>
                                                <PrivateRoute
                                                    path={"/create/choose-source"}
                                                    exact
                                                >
                                                    <SelectVocabularySource />
                                                </PrivateRoute>
                                                <PrivateRoute
                                                    path={"/create/choose-vocab-training-source"}
                                                    exact
                                                >
                                                    <SelectVocabularySourceVocabTraining />
                                                </PrivateRoute>
                                                <PrivateRoute
                                                    path={"/create/select-book"}
                                                    exact
                                                >
                                                    <SelectBookExperiment />
                                                </PrivateRoute>
                                                <PrivateRoute
                                                    path={"/create/select-own-source"}
                                                    exact
                                                >
                                                    <SelectOwnContent />
                                                </PrivateRoute>
                                                <PrivateRoute
                                                    path={"/create/select-vocabulary"}
                                                    exact
                                                >
                                                    <SelectBookVocabulary />
                                                </PrivateRoute>
                                                <PrivateRoute
                                                    path={"/create/summary"}
                                                    exact
                                                >
                                                    <Summary />
                                                </PrivateRoute>
                                                <PrivateRoute
                                                    path={"/create/rt-summary"}
                                                    exact
                                                >
                                                    <RecurringTaskSummary />
                                                </PrivateRoute>
                                                <Route path={"/create/preview/"}>
                                                    <ClassMemberContextProvider>
                                                        <StudentPracticeProvider>
                                                            <TestPreview />
                                                        </StudentPracticeProvider>
                                                    </ClassMemberContextProvider>
                                                </Route>
                                            </Switch>
                                            <WarningModal />
                                            <VocabTrainingLimitModal />
                                        </RTContextProvider>
                                    </BookContextProvider>
                                </Route>
                            </TestCreationContextProvider>
                            <Route path={"/review"}>
                                <TestReviewContextProvider>
                                    <Switch>
                                        <PrivateRoute
                                            path={"/review"}
                                            exact
                                        >
                                            <ReviewListing />
                                        </PrivateRoute>
                                        <PrivateRoute
                                            path={"/review/:testId/"}
                                            exact
                                        >
                                            <ReviewTestWrapper>
                                                <ReviewTest />
                                            </ReviewTestWrapper>
                                        </PrivateRoute>
                                        <PrivateRoute path={"/review/:testId/exercise/:exerciseId"}>
                                            <ReviewTestWrapper>
                                                <ReviewIndividualExercise />
                                            </ReviewTestWrapper>
                                        </PrivateRoute>
                                        <PrivateRoute path={"/review/:testId/student/:resultId"}>
                                            <ReviewTestWrapper>
                                                <ReviewIndividualStudent />
                                            </ReviewTestWrapper>
                                        </PrivateRoute>
                                        <PrivateRoute path={"/review/:testId/share"}>
                                            <ReviewTestWrapper>
                                                <ShareTestReviews />
                                            </ReviewTestWrapper>
                                        </PrivateRoute>
                                    </Switch>
                                </TestReviewContextProvider>
                            </Route>
                            <Route path={"/unauthorized"}>
                                <Unauthorized />
                            </Route>
                        </SurvicateWrapper>
                    </Switch>
                    <Toast.Viewport className={TOAST_VIEWPORT_CLASS} />
                </Toast.Provider>
            </BrowserRouter>
        </Background>
    );
};

export default Layout;
